import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { AirplaneTilt, ArrowRight, ClockCounterClockwise, MagnifyingGlass, MapPin, NavigationArrow } from '@phosphor-icons/react';
import type { Bbox } from '../types';
import { LAST_AREA_KEY, goToArea, useLocateMe } from '../geolocate';
import InstallPrompt from '../components/InstallPrompt';
import LocationHelp from '../components/LocationHelp';
import { apiUrl } from '../api';

const LIBERTY_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

interface Preset {
  name: string;
  code: string;
  bbox: Bbox;
}

const PRESETS: Preset[] = [
  { name: 'London Heathrow', code: 'EGLL', bbox: { lamin: 51.2, lomin: -0.75, lamax: 51.75, lomax: 0.4 } },
  { name: 'New York City', code: 'KJFK', bbox: { lamin: 40.4, lomin: -74.5, lamax: 41.0, lomax: -73.4 } },
  { name: 'SF Bay Area', code: 'KSFO', bbox: { lamin: 37.2, lomin: -122.7, lamax: 38.1, lomax: -121.7 } },
  { name: 'Amsterdam Schiphol', code: 'EHAM', bbox: { lamin: 52.0, lomin: 4.3, lamax: 52.6, lomax: 5.2 } },
  { name: 'Tokyo', code: 'RJTT', bbox: { lamin: 35.3, lomin: 139.3, lamax: 36.0, lomax: 140.3 } },
];

interface GeocodeResult {
  name: string;
  lat: number;
  lon: number;
  bbox: Bbox;
}


/** Rough on-screen extent of the current view, for the "you'll track this" readout. */
function viewExtentKm(map: maplibregl.Map): { w: number; h: number } {
  const b = map.getBounds();
  const midLat = ((b.getNorth() + b.getSouth()) / 2) * (Math.PI / 180);
  const w = Math.abs(b.getEast() - b.getWest()) * 111.32 * Math.cos(midLat);
  const h = Math.abs(b.getNorth() - b.getSouth()) * 110.57;
  return { w: Math.round(w), h: Math.round(h) };
}

export default function AreaPicker() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastArea, setLastArea] = useState<{ bbox: Bbox; name: string | null } | null>(null);
  const [extent, setExtent] = useState<{ w: number; h: number } | null>(null);
  const locateMe = useLocateMe();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_AREA_KEY);
      if (raw) setLastArea(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!mapContainer.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: LIBERTY_STYLE,
      center: [-0.1, 51.5],
      zoom: 7,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    const onMoveEnd = () => setExtent(viewExtentKm(map));
    map.on('load', onMoveEnd);
    map.on('moveend', onMoveEnd);
    return () => map.remove();
  }, []);

  const trackCurrentView = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    goToArea(
      { lamin: b.getSouth(), lomin: b.getWest(), lamax: b.getNorth(), lomax: b.getEast() },
      'Custom area',
    );
  };

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await fetch(apiUrl(`/api/geocode?q=${encodeURIComponent(query.trim())}`));
      if (!res.ok) throw new Error(`Search failed (HTTP ${res.status}). Try again in a moment.`);
      const data = (await res.json()) as { results: GeocodeResult[] };
      setResults(data.results);
      if (data.results.length === 0) setSearchError(`No places found for "${query.trim()}".`);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed. Try again in a moment.');
    } finally {
      setSearching(false);
    }
  };

  const flyToResult = (r: GeocodeResult) => {
    setResults([]);
    setQuery(r.name.split(',')[0]);
    mapRef.current?.fitBounds(
      [
        [r.bbox.lomin, r.bbox.lamin],
        [r.bbox.lomax, r.bbox.lamax],
      ],
      { padding: 40, duration: 1200, maxZoom: 10 },
    );
  };

  // adsb.fi and adsb.lol are point/radius APIs capped at 250 nm (see
  // server/src/sources/types.ts bboxToCircle), so past ~926 km diagonal only
  // OpenSky covers the corners.
  const tooWide = extent != null && Math.hypot(extent.w, extent.h) > 926;

  return (
    <div className="area-picker">
      <aside className="picker-panel">
        <header className="picker-brand">
          <span className="logo" aria-hidden="true">
            <AirplaneTilt size={18} weight="fill" />
          </span>
          <span className="wordmark">PlaneSight</span>
        </header>

        <div className="picker-intro">
          <h1>Pick a patch of sky to watch.</h1>
          <p>Live aircraft positions from OpenSky, adsb.fi and adsb.lol, merged onto one map.</p>
        </div>

        <form onSubmit={search} className="search-form" role="search">
          <label htmlFor="place-search" className="sr-only">
            Search for a place
          </label>
          <span className="search-form-icon" aria-hidden="true">
            <MagnifyingGlass size={16} weight="bold" />
          </span>
          <input
            id="place-search"
            type="text"
            value={query}
            placeholder="City, airport or region"
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" disabled={searching || !query.trim()}>
            {searching ? <span className="btn-spinner" aria-label="Searching" /> : 'Search'}
          </button>
        </form>
        <button type="button" className="locate-btn" onClick={locateMe.locate} disabled={locateMe.locating}>
          {locateMe.locating ? (
            <span className="btn-spinner" aria-hidden="true" />
          ) : (
            <NavigationArrow size={15} weight="fill" aria-hidden="true" />
          )}
          {locateMe.locating ? 'Finding you…' : 'Use my location'}
        </button>
        {locateMe.error && locateMe.errorKind ? (
          <LocationHelp
            kind={locateMe.errorKind}
            message={locateMe.error}
            permission={locateMe.errorPermission}
            detail={locateMe.errorDetail}
            onRetry={locateMe.locate}
            onDismiss={locateMe.clearError}
          />
        ) : (
          searchError && (
            <p className="search-error" role="alert">
              {searchError}
            </p>
          )
        )}
        {results.length > 0 && (
          <ul className="search-results" aria-label="Search results">
            {results.map((r, i) => (
              <li key={i} style={{ '--i': i } as React.CSSProperties}>
                <button onClick={() => flyToResult(r)}>
                  <MapPin size={15} weight="bold" aria-hidden="true" />
                  <span className="result-name">{r.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <section className="picker-section" aria-labelledby="presets-heading">
          <h2 id="presets-heading">Busy airspace</h2>
          <div className="presets">
            {PRESETS.map((p, i) => (
              <button
                key={p.name}
                className="preset"
                data-preset={p.name}
                onClick={() => goToArea(p.bbox, p.name)}
                style={{ '--i': i } as React.CSSProperties}
              >
                <span className="preset-code mono">{p.code}</span>
                <span className="preset-name">{p.name}</span>
              </button>
            ))}
          </div>
        </section>

        {lastArea && (
          <section className="picker-section" aria-labelledby="recent-heading">
            <h2 id="recent-heading">Pick up where you left off</h2>
            <button className="recent-area" onClick={() => goToArea(lastArea.bbox, lastArea.name)}>
              <ClockCounterClockwise size={16} weight="bold" aria-hidden="true" />
              <span>{lastArea.name ?? 'Last area'}</span>
              <span className="recent-arrow" aria-hidden="true">
                <ArrowRight size={15} weight="bold" />
              </span>
            </button>
          </section>
        )}

        <InstallPrompt variant="card" />

        <footer className="picker-footer">
          <button className="track-button" onClick={trackCurrentView}>
            Track this view
            <ArrowRight size={16} weight="bold" aria-hidden="true" />
          </button>
          <p className={tooWide ? 'is-warn' : ''}>
            {extent
              ? tooWide
                ? `This view is about ${extent.w.toLocaleString()} × ${extent.h.toLocaleString()} km. Two of the three feeds only reach 250 nm from the center, so zoom in for full coverage.`
                : `Pan and zoom the map, then track the ${extent.w.toLocaleString()} × ${extent.h.toLocaleString()} km you see.`
              : 'Pan and zoom the map to frame an area, then track it.'}
          </p>
        </footer>
      </aside>
      <div className="picker-map-wrap">
        <div ref={mapContainer} className="picker-map" />
        <div className="picker-frame" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}
